
import React, { useState } from "react";

const SCRIPT_URL =
"https://script.google.com/macros/s/AKfycbwOY_YHdCLEmjsyb51VujQUGJqUyCy4xSGsYeWLMH8trW-ceF3o3762CB5Y-ebky-QTig/exec";


export default function Career() {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [statusType, setStatusType] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();

    const form = e.currentTarget;
    const resume = form.elements.resume.files[0];

    if (!resume) {
      setStatus("Please upload your resume.");
      setStatusType("error");
      return;
    }

    const extension = resume.name.split(".").pop().toLowerCase();

    if (!["pdf", "doc", "docx"].includes(extension)) {
      setStatus("Please upload a PDF, DOC, or DOCX file.");
      setStatusType("error");
      return;
    }

    if (resume.size > 5 * 1024 * 1024) {
      setStatus("Resume must be 5 MB or smaller.");
      setStatusType("error");
      return;
    }

    setLoading(true);
    setStatus("");

    try {
      const resumeData = await new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
          resolve(String(reader.result).split(",")[1]);
        };

        reader.onerror = () => {
          reject(new Error("Unable to read your resume."));
        };

        reader.readAsDataURL(resume);
      });

      const application = {
        applicant_name: form.elements.applicant_name.value.trim(),
        applicant_email: form.elements.applicant_email.value.trim(),
        phone: form.elements.phone.value.trim(),
        position: form.elements.position.value.trim(),
        experience: form.elements.experience.value,
        gender: form.elements.gender.value,
        message: form.elements.message.value.trim(),
        resumeName: resume.name,
        resumeType: resume.type || "application/octet-stream",
        resumeData,
      };

      // Google Apps Script does not reliably support browser CORS responses.
      // no-cors lets the request be sent, but its response cannot be verified.
      await fetch(SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "text/plain;charset=utf-8",
        },
        body: JSON.stringify(application),
      });

      setStatus(
        "Your application request was sent. Please verify delivery before submitting again."
      );
      setStatusType("success");
      form.reset();
    } catch (error) {
      console.error("Career form error:", error);
      setStatus(
        error.message || "Unable to send your application. Please try again."
      );
      setStatusType("error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="career-section">
      <div className="career-container">
        <div className="career-heading">
          <span className="career-eyebrow">CAREERS</span>
          <h2>Join Our Team</h2>
          <p>
            Interested in working with us? Complete the form below and
            upload your resume. We look forward to hearing from you.
          </p>
        </div>

        <form className="career-form" onSubmit={handleSubmit}>
          <div className="career-grid">
            <div className="career-field">
              <label htmlFor="applicant_name">Full Name *</label>
              <input
                id="applicant_name"
                name="applicant_name"
                type="text"
                placeholder="Enter your full name"
                autoComplete="name"
                required
              />
            </div>

            <div className="career-field">
              <label htmlFor="applicant_email">Email Address *</label>
              <input
                id="applicant_email"
                name="applicant_email"
                type="email"
                placeholder="Enter your email address"
                autoComplete="email"
                required
              />
            </div>

            <div className="career-field">
              <label htmlFor="phone">Phone Number *</label>
              <input
                id="phone"
                name="phone"
                type="tel"
                placeholder="Enter your phone number"
                autoComplete="tel"
                required
              />
            </div>

            <div className="career-field">
              <label htmlFor="position">Position Applying For *</label>
              <input
                id="position"
                name="position"
                type="text"
                placeholder="Enter job position"
                required
              />
            </div>

            <div className="career-field">
              <label htmlFor="experience">Work Experience *</label>
              <select
                id="experience"
                name="experience"
                defaultValue=""
                required
              >
                <option value="" disabled>
                  Select experience
                </option>
                <option value="Fresher">Fresher</option>
                <option value="Less than 1 year">Less than 1 year</option>
                <option value="1-2 years">1–2 years</option>
                <option value="3-5 years">3–5 years</option>
                <option value="5+ years">5+ years</option>
              </select>
            </div>

            <div className="career-field">
              <label htmlFor="gender">Gender</label>
              <select id="gender" name="gender" defaultValue="">
                <option value="">Prefer not to say</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="career-field career-full-width">
              <label htmlFor="resume">
                Upload Resume * (PDF, DOC, DOCX — Max 5 MB)
              </label>
              <input
                id="resume"
                name="resume"
                type="file"
                accept=".pdf,.doc,.docx"
                required
              />
            </div>

            <div className="career-field career-full-width">
              <label htmlFor="message">Cover Letter / Message</label>
              <textarea
                id="message"
                name="message"
                rows={5}
                placeholder="Tell us about yourself..."
              />
            </div>
          </div>

          {status && (
            <div
              className={`career-status ${statusType}`}
              role="status"
              aria-live="polite"
            >
              {status}
            </div>
          )}

          <button
            type="submit"
            className="career-submit"
            disabled={loading}
          >
            {loading ? "Submitting..." : "Submit Application"}
          </button>

          <p className="career-privacy">
            Your information will be used to review your job application.
          </p>
        </form>
      </div>

      <style>{`
        .career-section {
          width: 100%;
          padding: 60px 20px;
          box-sizing: border-box;
          background: #f7f8fa;
          font-family: Arial, Helvetica, sans-serif;
          color: #0b1f33;
        }

        .career-container {
          width: 100%;
          max-width: 900px;
          margin: 0 auto;
        }

        .career-heading {
          text-align: center;
          margin-bottom: 32px;
        }

        .career-eyebrow {
          display: inline-block;
          margin-bottom: 12px;
          color: #015287;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 3px;
        }

        .career-heading h2 {
          margin: 0 0 14px;
          color: #0b1f33;
          font-size: clamp(30px, 4vw, 42px);
          line-height: 1.2;
          font-weight: 700;
        }

        .career-heading p {
          max-width: 650px;
          margin: 0 auto;
          color: #657180;
          font-size: 16px;
          line-height: 1.7;
        }

        .career-form {
          padding: 32px;
          background: #fff;
          border: 1px solid #e2e7ed;
          border-radius: 14px;
          box-shadow: 0 12px 36px rgba(11, 31, 51, 0.06);
        }

        .career-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 22px;
        }

        .career-field {
          display: flex;
          flex-direction: column;
          gap: 8px;
          min-width: 0;
        }

        .career-full-width {
          grid-column: 1 / -1;
        }

        .career-field label {
          color: #0b1f33;
          font-size: 14px;
          font-weight: 600;
        }

        .career-field input,
        .career-field select,
        .career-field textarea {
          width: 100%;
          min-height: 48px;
          padding: 12px 14px;
          box-sizing: border-box;
          border: 1px solid #d7dee6;
          border-radius: 7px;
          background: #fff;
          color: #263746;
          font: inherit;
          font-size: 15px;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .career-field input[type="file"] {
          padding: 10px;
          font-size: 13px;
        }

        .career-field textarea {
          min-height: 120px;
          resize: vertical;
        }

        .career-field input:focus,
        .career-field select:focus,
        .career-field textarea:focus {
          border-color: #015287;
          box-shadow: 0 0 0 3px rgba(1, 82, 135, 0.1);
        }

        .career-submit {
          width: 100%;
          margin-top: 24px;
          padding: 15px 22px;
          border: 0;
          border-radius: 7px;
          background: #015287;
          color: #fff;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          transition: background 0.2s, opacity 0.2s;
        }

        .career-submit:hover:not(:disabled) {
          background: #0b1f33;
        }

        .career-submit:disabled {
          cursor: not-allowed;
          opacity: 0.65;
        }

        .career-status {
          margin-top: 22px;
          padding: 13px 15px;
          border-radius: 7px;
          font-size: 14px;
          line-height: 1.6;
          overflow-wrap: anywhere;
        }

        .career-status.success {
          color: #176534;
          background: #eaf8ef;
          border: 1px solid #b9e6c7;
        }

        .career-status.error {
          color: #a32121;
          background: #fff0f0;
          border: 1px solid #f2c3c3;
        }

        .career-privacy {
          margin: 15px 0 0;
          color: #75808d;
          text-align: center;
          font-size: 12px;
          line-height: 1.6;
        }

        @media (max-width: 600px) {
          .career-section {
            padding: 40px 14px;
          }

          .career-form {
            padding: 22px 16px;
          }

          .career-grid {
            grid-template-columns: minmax(0, 1fr);
            gap: 18px;
          }

          .career-full-width {
            grid-column: auto;
          }

          .career-heading p {
            font-size: 14px;
          }
        }
      `}</style>
    </section>
  );
}
